import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import type { Sport } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { SBHorizontal } from '#src/atoms/layout/flex/index.tsx';
import { DotsLoader } from '#src/atoms/loader/DotsLoader/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { TermsDialog } from '#src/components/conversionCredit/TermsDialog/index.tsx';
import { MonetaryRewardDisclaimer } from '#src/components/jonum/MonetaryRewardDisclaimer/index.tsx';
import { DiscountBannerWrapper } from '#src/components/referral/DiscountBannerWrapper/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useEventsContext } from '#src/contexts/events/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useCurrentUserCurrencies } from '#src/hooks/useCurrentUserCurrencies.tsx';
import { useInviteLink } from '#src/hooks/useInviteLink.tsx';
import { LIFECYCLE, useLifecycle } from '#src/hooks/useLifecycle.ts';
import { useReferralProgramConfig } from '#src/hooks/useReferralProgramConfig.ts';
import { glossary } from '#src/lib/glossary.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const Buttons = styled(SBHorizontal)`
  width: 100%;
  @media ${tabletAndAbove} {
    width: auto;
  }
`;
const TermsLink = styled(ButtonBase)`
  color: var(--c-white);
  text-decoration: underline;
`;

type Props = {
  sport: Sport;
};
export const ReferralBanner = ({ sport }: Props) => {
  const { userFiatAmount: referralProgramConfigFiatAmount } =
    useReferralProgramConfig();
  const { currentUser } = useCurrentUserContext();
  const { fiatCurrency } = useCurrentUserCurrencies();
  const { up: isTabletOrDesktop } = useScreenSize('tablet');
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const { track } = useEventsContext();
  const inviteLink = useInviteLink(sport);
  const { update, lifecycle } = useLifecycle();

  const hideReferralBanner = lifecycle?.hideReferralBanner;

  if (!currentUser || hideReferralBanner) return null;

  const { referralRewardsCount } = currentUser;

  if (referralRewardsCount > 0) return null;

  const amount = referralProgramConfigFiatAmount && (
    <MonetaryRewardDisclaimer variant="monetary">
      <FormattedNumber
        value={referralProgramConfigFiatAmount}
        style="currency"
        currency={fiatCurrency}
        maximumFractionDigits={0}
      />
    </MonetaryRewardDisclaimer>
  );

  return (
    <DiscountBannerWrapper
      rightNode={
        <Buttons gap={1.5}>
          <Button
            size="small"
            color="transparent"
            fullWidth={!isTabletOrDesktop}
            onClick={() => {
              update(LIFECYCLE.hideReferralBanner, true);
            }}
          >
            <FormattedMessage defaultMessage="Dismiss" />
          </Button>
          <Button
            size="small"
            color="secondary"
            fullWidth={!isTabletOrDesktop}
            to={inviteLink}
            onClick={() => track('Click Referral Banner')}
          >
            <FormattedMessage defaultMessage="Refer a friend" />
          </Button>
        </Buttons>
      }
    >
      {referralProgramConfigFiatAmount === undefined ? (
        <DotsLoader />
      ) : (
        <BodyM as="span" color="var(--c-white)">
          <FormattedMessage
            defaultMessage="Get up to <b>{amount}</b> credit for each referred friend"
            values={{
              b: Bold,
              amount,
            }}
          />{' '}
          <TermsLink type="button" onClick={() => setOpenDialog(true)}>
            <FormattedMessage {...glossary.termsApply} />
          </TermsLink>
        </BodyM>
      )}
      {!!openDialog && <TermsDialog onClose={() => setOpenDialog(false)} />}
    </DiscountBannerWrapper>
  );
};
